import { useEffect, type ReactElement, type ReactNode } from 'react';

import styled, { css } from 'styled-components';

import { isEmpty } from '../../../services/checks';
import useHover from '../../../services/hooks/useHover';
import useLocalStorage from '../../../services/hooks/useLocalStorage';
import type { Nullable } from '../../../services/object';

import Body from '../../molecules/Body';
import Tooltip from '../../molecules/Tooltip';

const initialGap = 10;
const bannerWidth = 20;

type HighlightWrapperProps = Readonly<{
  side: 'left' | 'right';
  variant: 'default' | 'small'
}>;

const HighlightWrapper = styled.div<HighlightWrapperProps>`
  ${({ variant, theme, side }) => {
    if (variant === 'default') {
      return css`
      display: contents;
        & > *:first-child {
          box-shadow: ${({ theme }) => theme.scTheme.shadows.focus.warning.full};
          overflow: hidden;
          position: relative;

          &::after {
            aspect-ratio: 1;
            background: linear-gradient(
              ${side === 'left' ? '135deg' : '-135deg'},
              transparent 0,
              transparent ${initialGap}px,
              ${theme.scTheme.palette.support.warning[40]} ${initialGap}px,
              ${theme.scTheme.palette.support.warning[40]} ${bannerWidth + initialGap}px,
              transparent ${bannerWidth + initialGap}px
            );
            content: '';
            left: ${side === 'left' ? '0' : undefined};
            margin: 0;
            padding: 0;
            position: absolute;
            right: ${side === 'right' ? '0' : undefined};
            top: 0;
            width: 100%;
            z-index: 1;
          }

          &::before {
            color: white;
            content: 'NEW!';
            font-size: ${theme.scTheme.fontSizes[200]};
            left: ${side === 'left' ? '0' : undefined};
            position: absolute;
            right: ${side === 'right' ? '0' : undefined};
            top: ${side === 'left' ? initialGap / 2 : initialGap}px;
            transform: rotate(${side === 'left' ? '-45deg' : '45deg'});
            z-index: 2;
          }
        }
      `
    } else if (variant === 'small') {
      return css`
        position: relative;

        &::before {
          color: white;
          content: 'New!';
          font-size: ${theme.scTheme.fontSizes[200]};
          left: 50%;
          position: absolute;
          top: calc(${({ theme }) => theme.scTheme.space[300]} * -1);
          transform: translate(-50%, -100%);
          padding: ${({ theme }) => `${theme.scTheme.space[100]} ${theme.scTheme.space[200]}`};
          border-radius: ${({ theme }) => theme.scTheme.borders.radii.md};
          background-color: ${theme.scTheme.palette.support.warning[40]};
          z-index: 4;
        }

        & > *:first-child {
          box-shadow: ${({ theme }) => theme.scTheme.shadows.focus.warning.full};
        }
      `
    }

    return null;
  }}
`;

type Props = Readonly<{
  featureKey: string;
  children: ReactNode;
  caption?: Nullable<string>;
  variant?: HighlightWrapperProps['variant'];
  side?: 'left' | 'right';
}>;

const NewFeatureHighlight = ({
  featureKey, children, caption, side = 'left', variant = 'default',
}: Props): ReactElement => {
  const [seen, setSeen] = useLocalStorage(featureKey, false);
  const [ref, hovered] = useHover<HTMLDivElement>();

  useEffect(() => {
    if (hovered) {
      setSeen(true);
    }
  }, [hovered, setSeen]);

  if (seen || hovered) {
    return <>{children}</>;
  }

  return (
    <Tooltip
      enabled={!isEmpty(caption)}
      tooltipProps={{
        title: <Body size={200} inverted>{caption}</Body>,
      }}
    >
      <HighlightWrapper ref={ref} side={side} variant={variant}>
        {children}
      </HighlightWrapper>
    </Tooltip>

  );
};

export default NewFeatureHighlight;
